
import Navbar from "@/component/navbar";
import { showAlert } from '@/components/AppDialog';

import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from "react-i18next";

// Stripe payment links for each plan
const STRIPE_PAYMENT_LINKS = {
    starter: 'https://buy.stripe.com/test_00wbJ2gCN49tfuU76CaVa00',
    growth: 'https://buy.stripe.com/test_3cI7sMaepbBV1E4bmSaVa01',
    scale: 'https://buy.stripe.com/test_28EfZieuF0Xh3Mc4YuaVa02'
};

export default function Pricing() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState<'starter' | 'growth' | 'scale' | null>(null);
    const [showDemoModal, setShowDemoModal] = useState(false);

    // select a plan (no immediate redirect)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handlePlanSelection = (plan: 'starter' | 'growth' | 'scale') => {
        setSelectedPlan(plan);
    };

    // proceed: show demo modal (per new UX) instead of immediate payment redirect
    const handleProceed = () => {
        if (!selectedPlan) {
            showAlert(t('pricing.selectPlan'));
            return;
        }
        setShowDemoModal(true);
    };

    return (
        <>
            <Navbar />


            {/* Pricing Section */}
            <section className="bg-white py-20 px-8 md:px-16 lg:px-24 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            {t('pricing.title')}
                        </h1>

                    </div>

                    {/* pricing cards follow; bottom nav relocated to page bottom */}

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">

                        {/* Starter Pack */}
                        <div
                            onClick={() => setSelectedPlan('starter')}
                            className={`flex flex-col h-full border-2 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer ${selectedPlan === 'starter' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                                }`}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.starter.name')}</h3>
                                    <p className="text-sm text-gray-600">{t('pricing.starter.duration')}</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-1">{t('pricing.starter.badge')}</p>
                                </div>
                                <div className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${selectedPlan === 'starter' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                                    }`}>
                                    {selectedPlan === 'starter' && (
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-8 flex-1">
                                <p className="text-sm text-gray-700 mb-4">
                                    {t('pricing.starter.desc')}
                                </p>

                                <h4 className="font-semibold text-gray-900 mb-3">{t('pricing.starter.deliverables')}</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.starter.item1')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.starter.item2')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>Business Plan Lite (Lean Canvas + narrative brief, 6–10 pages)</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="border-t border-gray-200 pt-6 mb-6">
                                <div className="text-4xl font-bold text-gray-900">{t('pricing.starter.price')}</div>
                            </div>

                            {/* card selectable; CTA moved to bottom navigation */}
                        </div>

                        {/* Growth Pack */}
                        <div
                            onClick={() => setSelectedPlan('growth')}
                            className={`flex flex-col h-full border-2 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer ${selectedPlan === 'growth' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                                }`}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.growth.name')}</h3>
                                    <p className="text-sm text-gray-600">{t('pricing.growth.duration')}</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-1">{t('pricing.growth.badge')}</p>
                                </div>
                                <div className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${selectedPlan === 'growth' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                                    }`}>
                                    {selectedPlan === 'growth' && (
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-8 flex-1">
                                <p className="text-sm text-gray-700 mb-4">
                                    {t('pricing.growth.desc')}
                                </p>

                                <h4 className="font-semibold text-gray-900 mb-3">{t('pricing.starter.deliverables')}</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.growth.item1')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.growth.item2')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.growth.item3')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.growth.item4')}</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="border-t border-gray-200 pt-6 mb-6">
                                <div className="text-4xl font-bold text-gray-900">{t('pricing.growth.price')}</div>
                            </div>

                            {/* card selectable; CTA moved to bottom navigation */}
                        </div>

                        {/* Scale Pack - Featured */}
                        <div
                            onClick={() => setSelectedPlan('scale')}
                            className={`flex flex-col h-full border-2 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer ${selectedPlan === 'scale' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                                }`}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="inline-block bg-blue-600 text-white text-xs px-3 py-1 rounded-t-md mb-2">{t('pricing.mostPopular')}</div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.pro.name')}</h3>
                                    <p className="text-sm text-gray-600">{t('pricing.pro.duration')}</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-1">{t('pricing.pro.badge')}</p>
                                </div>
                                <div className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all ${selectedPlan === 'scale' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                                    }`}>
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                </div>
                            </div>

                            <div className="mb-8 flex-1">
                                <p className="text-sm text-gray-700 mb-4">
                                    {t('pricing.pro.desc')}
                                </p>

                                <h4 className="font-semibold text-gray-900 mb-3">{t('pricing.starter.deliverables')}</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.pro.item1')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.pro.item2')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.pro.item3')}</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-green-600 mt-0.5">✓</span>
                                        <span>{t('pricing.pro.item4')}</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="border-t border-gray-200 pt-6 mb-6">
                                <div className="flex items-baseline gap-2">
                                    <div className="text-4xl font-bold text-gray-900">{t('pricing.pro.price')}</div>
                                    <div className="text-sm text-gray-600 line-through">$13,999</div>
                                </div>
                                <p className="text-xs text-blue-600 mt-1">{t('pricing.save')} ({t('pricing.fourPayments')})</p>
                            </div>

                            {/* card selectable; CTA moved to bottom navigation */}
                        </div>

                    </div>

                </div>

                {/* Bottom navigation: left-aligned Back and Next */}
                <div className="max-w-6xl mx-auto mt-8 flex items-center justify-start gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                    >
                        ◂ {t('pricing.back')}
                    </button>

                    <button
                        onClick={handleProceed}
                        disabled={!selectedPlan}
                        className={`px-6 py-3 rounded-lg text-sm font-semibold ${selectedPlan ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-100 text-blue-400 cursor-not-allowed'}`}
                    >
                        {selectedPlan ? t('pricing.continue') : t('pricing.next')}
                    </button>
                </div>

                {/* Demo modal (appears when Next is clicked) */}
                {showDemoModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                        <div className="absolute inset-0 bg-black opacity-40" onClick={() => setShowDemoModal(false)}></div>
                        <div className="bg-white rounded-md shadow-lg z-60 w-full max-w-xl mx-4 p-6 relative">
                            <h3 className="text-xl font-semibold mb-2 text-black">{t('pricing.modal.title')}</h3>
                            <p className="text-sm text-gray-700 mb-6">{t('pricing.modal.description')}</p>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                                    onClick={() => {
                                        // If a plan is selected, redirect to the Stripe payment link
                                        if (selectedPlan && STRIPE_PAYMENT_LINKS[selectedPlan]) {
                                            // Use location replace so back button doesn't return to modal
                                            window.location.href = STRIPE_PAYMENT_LINKS[selectedPlan];
                                            return;
                                        }
                                        // Otherwise just close the modal
                                        setShowDemoModal(false);
                                    }}
                                >
                                    {t('pricing.modal.continue')}
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                    onClick={() => { setShowDemoModal(false); navigate('/raising/schedule'); }}
                                >
                                    {t('pricing.modal.bookDemo')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>


        </>
    );
}
